'use client'

/**
 * MiniBrowser — the complete askFinz mini-browser, reusable by any app on askfinz.com or *.askfinz.ai.
 *
 * `MiniBrowserFrame` (this file's sibling) is the render engine: it decides which of the four tiers
 * paints a page. THIS component is everything around it — tabs, navigation, the URL bar, pane
 * layouts, and the per-page tools — so an app can embed a real browser without reimplementing any
 * of it.
 *
 * WHY IT LIVES HERE. All of this previously existed only inside
 * `search.askfinz.ai/app/components/SearchView.tsx`, tangled through 5,373 lines that also hold the
 * search UI. Every other app that wanted to show a live page either re-invented a worse version or
 * hard-pinned the server proxy — which is exactly what made the indexing inspector show a
 * broken-file icon on every PDF, and what made github.com un-inspectable. One implementation, one
 * place to fix.
 *
 * STATE IS OWNED HERE and can be lifted out with `tabs`/`onTabsChange` when the host needs to
 * persist it (SearchView stores tabs across reloads; the crawl-auth page does not care).
 *
 * The tools that need to reach INSIDE the page — narrate, translate, and the coverage overlay used
 * by the indexing inspector — go through the extension's `iframe_bridge.js`, which runs in every
 * frame and accepts commands from an askfinz.ai parent. That is the reason they work on the direct
 * tier at all: the parent cannot touch a cross-origin document, but the bridge inside it can.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft, ArrowRight, RotateCcw, Lock, Star, Square, Columns2, Grid2x2,
  Volume2, Languages, Bug, ExternalLink, Plus, X, User,
} from 'lucide-react'
import { MiniBrowserFrame, useExtensionDetected, type FrameTier } from './mini-browser-frame'

export interface BrowserTab {
  id: string
  url: string
  title: string
  loading?: boolean
  history: string[]
  historyIndex: number
  refreshKey: number
  favicon?: string
}

export type PaneMode = 'single' | 'split' | 'grid'

export interface MiniBrowserProps {
  /** Pages to open on first mount. */
  initialUrls?: string[]
  /** Controlled tabs. Omit to let the component own them. */
  tabs?: BrowserTab[]
  onTabsChange?: (tabs: BrowserTab[]) => void
  activeTabId?: string | null
  onActiveTabChange?: (id: string | null) => void
  paneMode?: PaneMode
  onPaneModeChange?: (m: PaneMode) => void
  profileLabel?: string
  className?: string
  /** Hide chrome an embedder does not want. Everything defaults ON. */
  show?: Partial<Record<
    'tabs' | 'nav' | 'urlbar' | 'bookmark' | 'panes' | 'narrate' | 'translate' | 'report' | 'openExternal',
    boolean
  >>
  onBookmark?: (tab: BrowserTab) => void
  onReportIssue?: (tab: BrowserTab) => void
  onTierChange?: (tabId: string, tier: FrameTier) => void
  /** Forces the same-origin proxy. Only for callers that must READ the document from outside. */
  forceProxy?: boolean
  proxyPath?: string
  frameCheckPath?: string
}

const favicon = (url: string, sz = 32) => {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=${sz}`
  } catch {
    return ''
  }
}
const hostOf = (url: string) => { try { return new URL(url).hostname } catch { return url } }
const pathOf = (url: string) => { try { return new URL(url).pathname } catch { return '' } }

/** `example.com` → `https://example.com`; a search term is left alone for the host to handle. */
function normaliseUrl(input: string): string {
  const s = (input || '').trim()
  if (!s) return ''
  if (/^https?:\/\//i.test(s)) return s
  if (/^[\w-]+(\.[\w-]+)+(\/|$|\?|#)/.test(s)) return `https://${s}`
  return s
}

let seq = 0
const newId = () => `tab-${Date.now().toString(36)}-${(seq++).toString(36)}`

export function MiniBrowser({
  initialUrls = [],
  tabs: tabsProp,
  onTabsChange,
  activeTabId: activeProp,
  onActiveTabChange,
  paneMode: paneProp,
  onPaneModeChange,
  profileLabel = 'Default',
  className = '',
  show = {},
  onBookmark,
  onReportIssue,
  onTierChange,
  forceProxy = false,
  proxyPath,
  frameCheckPath,
}: MiniBrowserProps) {
  const vis = {
    tabs: true, nav: true, urlbar: true, bookmark: true, panes: true,
    narrate: true, translate: true, report: true, openExternal: true, ...show,
  }

  const [tabsState, setTabsState] = useState<BrowserTab[]>(() =>
    initialUrls.map((u) => ({
      id: newId(), url: normaliseUrl(u), title: hostOf(u), history: [normaliseUrl(u)],
      historyIndex: 0, refreshKey: 0, loading: true,
    })),
  )
  const [activeState, setActiveState] = useState<string | null>(null)
  const [paneState, setPaneState] = useState<PaneMode>('single')
  const [editingUrl, setEditingUrl] = useState<string | null>(null)
  // One STABLE ref object per tab. A synthetic {get current/set current} cast typechecks but is
  // fragile — React writes to `.current` directly, and a new object each render would leave the
  // previous frame unreachable, silently breaking narrate/translate/overlay for that tab.
  const frameRefs = useRef<Map<string, React.RefObject<HTMLIFrameElement | null>>>(new Map())
  const frameRefFor = useCallback((id: string) => {
    let r = frameRefs.current.get(id)
    if (!r) { r = { current: null }; frameRefs.current.set(id, r) }
    return r
  }, [])
  const extension = useExtensionDetected()

  const tabs = tabsProp ?? tabsState
  const paneMode = paneProp ?? paneState
  const activeTabId = activeProp ?? activeState ?? tabs[0]?.id ?? null
  const active = tabs.find((t) => t.id === activeTabId) || null

  const setTabs = useCallback((next: BrowserTab[] | ((p: BrowserTab[]) => BrowserTab[])) => {
    const resolve = (p: BrowserTab[]) => (typeof next === 'function' ? (next as (p: BrowserTab[]) => BrowserTab[])(p) : next)
    if (tabsProp && onTabsChange) onTabsChange(resolve(tabsProp))
    else setTabsState(resolve)
  }, [tabsProp, onTabsChange])

  const setActive = useCallback((id: string | null) => {
    onActiveTabChange ? onActiveTabChange(id) : setActiveState(id)
  }, [onActiveTabChange])

  const setPane = useCallback((m: PaneMode) => {
    onPaneModeChange ? onPaneModeChange(m) : setPaneState(m)
  }, [onPaneModeChange])

  useEffect(() => {
    if (!activeTabId && tabs.length) setActive(tabs[0].id)
  }, [activeTabId, tabs, setActive])

  useEffect(() => {
    const live = new Set(tabs.map((t) => t.id))
    frameRefs.current.forEach((_, id) => { if (!live.has(id)) frameRefs.current.delete(id) })
  }, [tabs])

  const patch = useCallback((id: string, p: Partial<BrowserTab>) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, ...p } : t)))
  }, [setTabs])

  // ── navigation ────────────────────────────────────────────────────────────
  // History is tracked here rather than read from the iframe: `contentWindow.history` is
  // cross-origin on three of the four tiers, so asking the frame would work only on the proxy —
  // the same mistake that made the inspector force the weakest tier.
  const navigate = useCallback((id: string, raw: string) => {
    const url = normaliseUrl(raw)
    if (!url) return
    setTabs((prev) => prev.map((t) => {
      if (t.id !== id) return t
      const hist = [...t.history.slice(0, t.historyIndex + 1), url]
      return { ...t, url, title: hostOf(url), loading: true, history: hist, historyIndex: hist.length - 1 }
    }))
  }, [setTabs])

  const go = useCallback((id: string, delta: number) => {
    setTabs((prev) => prev.map((t) => {
      if (t.id !== id) return t
      const i = t.historyIndex + delta
      if (i < 0 || i >= t.history.length) return t
      return { ...t, url: t.history[i], historyIndex: i, title: hostOf(t.history[i]), loading: true }
    }))
  }, [setTabs])

  const reload = useCallback((id: string) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, refreshKey: t.refreshKey + 1, loading: true } : t)))
  }, [setTabs])

  const openTab = useCallback((url = '') => {
    const id = newId()
    const u = normaliseUrl(url)
    setTabs((prev) => [...prev, {
      id, url: u, title: u ? hostOf(u) : 'New Tab',
      history: u ? [u] : [], historyIndex: u ? 0 : -1, refreshKey: 0, loading: !!u,
    }])
    setActive(id)
    return id
  }, [setTabs, setActive])

  const closeTab = useCallback((id: string) => {
    setTabs((prev) => {
      const i = prev.findIndex((t) => t.id === id)
      const next = prev.filter((t) => t.id !== id)
      if (id === activeTabId) setActive(next[Math.max(0, i - 1)]?.id ?? null)
      return next
    })
  }, [setTabs, activeTabId, setActive])

  // Drag to reorder.
  const dragFrom = useRef<number | null>(null)
  const onDrop = (to: number) => {
    const from = dragFrom.current
    dragFrom.current = null
    if (from === null || from === to) return
    setTabs((prev) => {
      const next = [...prev]
      const [m] = next.splice(from, 1)
      next.splice(to, 0, m)
      return next
    })
  }

  /** Commands that must run INSIDE the page. Only the extension's in-frame bridge can do this. */
  const tellFrame = useCallback((id: string, msg: Record<string, unknown>) => {
    try { frameRefs.current.get(id)?.current?.contentWindow?.postMessage(msg, '*') } catch { /* frame gone */ }
  }, [])

  const panes = useMemo(() => (paneMode === 'single' ? 1 : paneMode === 'split' ? 2 : 4), [paneMode])
  const visible = useMemo(() => {
    if (paneMode === 'single') return active ? [active] : []
    const i = Math.max(0, tabs.findIndex((t) => t.id === activeTabId))
    return tabs.slice(i, i + panes)
  }, [paneMode, tabs, activeTabId, active, panes])

  const btn = 'p-1 rounded transition-colors hover:bg-white/10 text-text-secondary disabled:opacity-20'

  return (
    <div className={`flex flex-col min-h-0 h-full overflow-hidden bg-bg-main ${className}`}>
      {/* ── tab bar ─────────────────────────────────────────────────────── */}
      {vis.tabs && (
        <div data-tabbar className="flex items-center justify-between border-b border-border-color bg-[#0a0a0a]" style={{ minHeight: 36 }}>
          <div className="flex flex-1 min-w-0 items-center gap-0 overflow-x-auto invisible-scrollbar h-full">
            {tabs.map((t, i) => (
              <div
                key={t.id}
                draggable
                onDragStart={() => { dragFrom.current = i }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                onClick={() => setActive(t.id)}
                className={`group flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium cursor-grab active:cursor-grabbing border-r border-border-color/30 max-w-[200px] min-w-[120px] transition-all ${
                  t.id === activeTabId ? 'bg-[#1a1a1a] text-white' : 'text-gray-400 hover:bg-white/5'
                }`}
              >
                {t.url && <img alt="" className="w-3.5 h-3.5 rounded-sm shrink-0" src={t.favicon || favicon(t.url, 16)} />}
                <span className="truncate flex-1">{t.title || hostOf(t.url) || 'New Tab'}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); closeTab(t.id) }}
                  className="p-0.5 rounded hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  title="Close tab"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
            <div className="flex items-center ml-1 shrink-0">
              <button onClick={() => openTab()} className="p-1.5 shrink-0 transition-colors rounded hover:bg-[#222] text-gray-500 hover:text-gray-300" title="New tab">
                <Plus size={14} />
              </button>
            </div>
          </div>
          <div className="relative shrink-0 pr-2 pl-2 border-l border-border-color/20">
            <div
              className="flex items-center gap-1.5 px-2 py-0.5 mx-1 rounded-full cursor-pointer transition-transform active:scale-95 text-[10px] font-bold uppercase tracking-widest border shadow-sm bg-white/5 text-gray-400 border-white/10 hover:border-white/30 hover:text-gray-200"
              title={`Active Profile: ${profileLabel}`}
            >
              <User size={10} />{profileLabel}
            </div>
          </div>
        </div>
      )}

      {/* ── toolbar ─────────────────────────────────────────────────────── */}
      {(vis.nav || vis.urlbar) && active && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border-color relative bg-[#111]">
          {vis.nav && (
            <>
              <button className={btn} title="Go back" disabled={active.historyIndex <= 0} onClick={() => go(active.id, -1)}>
                <ArrowLeft size={14} />
              </button>
              <button className={btn} title="Go forward" disabled={active.historyIndex >= active.history.length - 1} onClick={() => go(active.id, 1)}>
                <ArrowRight size={14} />
              </button>
              <button className={btn} title="Reload" onClick={() => reload(active.id)}>
                <RotateCcw size={12} />
              </button>
            </>
          )}

          {vis.urlbar && (
            <div className="flex-1 min-w-0 flex items-center gap-1.5 text-xs px-2 py-1 rounded cursor-text truncate border border-transparent hover:border-border-color/50 bg-[#1a1a1a]">
              <button className="shrink-0 p-0.5 rounded transition-colors text-emerald-500 hover:bg-emerald-500/10" title="Connection is secure">
                <Lock size={12} />
              </button>
              {active.url && <img className="w-3.5 h-3.5 rounded-sm shrink-0" alt="" src={active.favicon || favicon(active.url)} />}
              {editingUrl === null ? (
                <span className="truncate flex-1" onClick={() => setEditingUrl(active.url)}>
                  <span className="text-white font-medium">{hostOf(active.url)}</span>
                  <span className="text-gray-500">{pathOf(active.url)}</span>
                </span>
              ) : (
                <input
                  autoFocus
                  className="flex-1 bg-transparent outline-none text-white min-w-0"
                  value={editingUrl}
                  onChange={(e) => setEditingUrl(e.target.value)}
                  onBlur={() => setEditingUrl(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { navigate(active.id, editingUrl); setEditingUrl(null) }
                    if (e.key === 'Escape') setEditingUrl(null)
                  }}
                />
              )}
            </div>
          )}

          {vis.bookmark && (
            <button className={`${btn} shrink-0 hover:text-amber-400 text-gray-500`} title="Bookmark this page" onClick={() => onBookmark?.(active)}>
              <Star size={13} />
            </button>
          )}

          {vis.panes && (
            <div className="flex p-0.5 rounded shrink-0 border ml-1 mr-0.5 bg-black/30 border-white/5">
              {([['single', Square, 'Single Pane Mode'], ['split', Columns2, 'Split Pane (1x2)'], ['grid', Grid2x2, 'Grid Pane (2x2)']] as const).map(
                ([m, Icon, title]) => (
                  <button
                    key={m}
                    onClick={() => setPane(m)}
                    title={title}
                    className={`p-1 w-[22px] h-[22px] flex items-center justify-center rounded transition-colors ${
                      paneMode === m ? 'bg-blue-500/20 text-blue-500' : 'hover:bg-white/10 text-text-secondary'
                    }`}
                  >
                    <Icon size={m === 'single' ? 11 : 12} strokeWidth={2.5} />
                  </button>
                ),
              )}
            </div>
          )}

          {/* These reach INSIDE the page, so they need the extension's in-frame bridge. Disabled
              rather than hidden without it — a control that silently does nothing is worse. */}
          {vis.narrate && (
            <button className={`${btn} shrink-0`} title={extension ? 'Audio Narrator' : 'Audio Narrator — needs the askFinz extension'}
                    disabled={!extension} onClick={() => tellFrame(active.id, { type: 'EXTRACT_TEXT_FOR_SPEECH' })}>
              <Volume2 size={13} />
            </button>
          )}
          {vis.translate && (
            <button className={`${btn} shrink-0`} title={extension ? 'Translate page' : 'Translate — needs the askFinz extension'}
                    disabled={!extension} onClick={() => tellFrame(active.id, { type: 'TRANSLATE_PAGE', targetLang: 'en' })}>
              <Languages size={13} />
            </button>
          )}
          {vis.report && (
            <button className={`${btn} shrink-0`} title="Report a rendering issue" onClick={() => onReportIssue?.(active)}>
              <Bug size={13} />
            </button>
          )}
          {vis.openExternal && (
            <a href={active.url} target="_blank" rel="noreferrer" className={`${btn} shrink-0`} title="Open in new browser tab">
              <ExternalLink size={13} />
            </a>
          )}
        </div>
      )}

      {/* ── pages ───────────────────────────────────────────────────────── */}
      <div
        className={`flex-1 relative bg-bg-main overflow-hidden min-h-0 ${
          paneMode === 'single' ? '' : paneMode === 'split' ? 'grid grid-cols-2' : 'grid grid-cols-2 grid-rows-2'
        }`}
        data-browser-area
      >
        {visible.map((t) => (
          // HEIGHT. In single-pane mode the container is `flex-1 relative` — a block box, not a
          // flex or grid parent — so a child whose only content is an ABSOLUTELY positioned iframe
          // collapses to height 0. Measured 2026-08-05 on bloomberg.com: the page loaded fine (12
          // child frames, real network activity) and rendered as a blank strip, because the frame
          // was 1303x0. Checking the iframe's `src` says nothing about whether anything is on
          // screen — only the measured box does.
          // `h-full` fixes single-pane; split/grid children already stretch to their track.
          <div
            key={t.id}
            className={`relative min-h-0 border-border-color/40 border-r border-b last:border-r-0 ${
              paneMode === 'single' ? 'h-full w-full' : ''
            }`}
          >
            <MiniBrowserFrame
              url={t.url}
              refreshKey={t.refreshKey}
              className="absolute inset-0 w-full h-full border-0 bg-white"
              title={t.title || hostOf(t.url)}
              forceProxy={forceProxy}
              proxyPath={proxyPath}
              frameCheckPath={frameCheckPath}
              frameRef={frameRefFor(t.id)}
              onTierChange={(tier) => onTierChange?.(t.id, tier)}
              onLoad={() => patch(t.id, { loading: false })}
            />
          </div>
        ))}
        {!visible.length && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-text-secondary">
            No page open.
          </div>
        )}
      </div>
    </div>
  )
}

export default MiniBrowser
